// Was die Karten Bestellung, Dachpartie und Bodenpartie gemeinsam haben
// (E3c, S3 und S4): das Schicken an den Server (mit Meldung an der Stelle),
// die Wahl eines Lieferanten oder einer Partie durch Antippen, „Text
// kopieren“, „schon erledigt“ mit Rückfrage, den Excel-Report von K2
// hochladen, die Schritte einer Mail mit dem Hinweis „Noch von Hand“ – und
// eine Fehlergrenze, die einen Fehler der Karte ohne
// Kundendaten an die Fehlerwache meldet (ablauf.ts, ablaufFehlerFuerWache).

import { Component, useCallback, useState, type ReactNode } from "react";
import { type BueroAntwort, type PartnerZeile } from "@happy-energy/kern";
import { ApiFehler, fehlerMelden, Ikon, OhneVerbindung, senden, Tipp, toastZeigen } from "@happy-energy/oberflaeche";
import { meldungAus } from "../meldung.ts";
import { anderenFensternMelden } from "../speicher.ts";
import { ablaufFehlerFuerWache, mitMailAdresse } from "./ablauf.ts";
import { k2ExcelAnhaengen } from "./k2-excel.ts";

export type KartenName = "Bestellung" | "Dachpartie" | "Bodenpartie" | "Dach-Unterlage";

/** Ein Fehler, den niemand erwartet (kein Nein vom Server, keine fehlende Verbindung) – an die Fehlerwache, ohne Kundendaten. */
export function unerwartetMelden(fehler: unknown, wo: KartenName): void {
  if (fehler instanceof ApiFehler || fehler instanceof OhneVerbindung) return;
  fehlerMelden("crm", ablaufFehlerFuerWache(fehler, wo));
}

/**
 * Schickt eine Änderung an der Anlage (POST /api/crm/anlage/:id/<pfad>) –
 * danach lädt die Akte neu, und die anderen Fenster erfahren es („Zu tun“).
 * Ein Nein des Servers (409) steht als Satz an der Karte, und die Akte lädt
 * neu: Dann hat sich inzwischen etwas geändert.
 */
export function useSchicken(anlageId: string, wo: KartenName, neuLaden: () => void) {
  const [sendet, setSendet] = useState(false);
  const [meldung, setMeldung] = useState<string | null>(null);
  const schicken = useCallback(
    async (pfad: string, body: unknown, fertig?: string): Promise<boolean> => {
      setSendet(true);
      setMeldung(null);
      try {
        await senden<BueroAntwort>(`/api/crm/anlage/${anlageId}/${pfad}`, body);
        if (fertig) toastZeigen(fertig);
        anderenFensternMelden(anlageId);
        neuLaden();
        return true;
      } catch (f) {
        unerwartetMelden(f, wo);
        setMeldung(meldungAus(f));
        if (f instanceof ApiFehler && f.status === 409) neuLaden();
        return false;
      } finally {
        setSendet(false);
      }
    },
    [anlageId, wo, neuLaden],
  );
  return { sendet, meldung, schicken, setMeldung };
}

/** Die Meldung an der Karte, wenn etwas nicht ging. */
export function Meldung({ text }: { text: string | null }) {
  if (!text) return null;
  return (
    <p className="crm-fehler-satz" role="alert">
      {text}
    </p>
  );
}

/**
 * Lieferant oder Partie zum Antippen – die angebotenen, dazu der gewählte,
 * auch wenn er nicht mehr angeboten wird (Befund 8: er steht an der Zeile
 * weiter da). Keiner gewählt: keiner hervorgehoben.
 */
export function PartnerWahl({
  name,
  partner,
  gewaehlt,
  waehlen,
  gesperrt,
}: {
  name: string;
  partner: readonly PartnerZeile[];
  gewaehlt: string | null;
  waehlen: (id: string) => void;
  gesperrt: boolean;
}) {
  const zeigen = partner.filter((p) => p.aktiv || p.id === gewaehlt);
  if (zeigen.length === 0) {
    return <p className="leise klein">Noch keine eingetragen – die Leitung trägt sie unter Einstellungen → „Lieferanten und Partien“ ein.</p>;
  }
  // Gernots Augentest E3c (3.11.2026): Eine einzelne, noch nicht gewählte Partie sah aus, als wäre sie schon eingeteilt – deshalb sagt es ein Satz darüber.
  return (
    <div className="ablauf-waehlen">
      {gewaehlt === null && <span className="akte-geaendert klein">Noch nicht gewählt – bitte antippen:</span>}
      <div className="auswahl ablauf-wahl" role="radiogroup" aria-label={name}>
        {zeigen.map((p) => (
          <button
            key={p.id}
            type="button"
            role="radio"
            aria-checked={p.id === gewaehlt}
            className={p.id === gewaehlt ? "an" : undefined}
            disabled={gesperrt}
            onClick={() => {
              if (p.id !== gewaehlt) waehlen(p.id);
            }}
          >
            {p.name}
            {!p.aktiv && <span className="ablauf-nicht-mehr"> (nicht mehr angeboten)</span>}
          </button>
        ))}
      </div>
    </div>
  );
}

/**
 * „An:“ über der Mail. Hat der Lieferant oder die Partie mehrere
 * Ansprechpartner mit Mail, wird einer angetippt – die Mail geht nur an ihn,
 * die Anrede nennt nur ihn (Gernot, Augentest E3c, 3.11.2026); vorgewählt
 * ist der erste. `an`: die Adressen der fertigen Mail.
 */
export function AnWen({ p, an, nr, waehlen }: { p: PartnerZeile; an: readonly string[]; nr: number; waehlen: (nr: number) => void }) {
  const moeglich = mitMailAdresse(p);
  const zeile = (
    <p className="klein">
      <span className="leise">An:</span> {an.length > 0 ? an.join(", ") : `${p.name} hat keine Mail-Adresse eingetragen`}
    </p>
  );
  if (moeglich.length < 2) return zeile;
  const gewaehlt = nr < moeglich.length ? nr : 0;
  return (
    <>
      <div className="ablauf-an">
        <span className="leise klein">An wen?</span>
        <div className="auswahl ablauf-wahl" role="radiogroup" aria-label={`Ansprechpartner bei ${p.name}`}>
          {moeglich.map((a, i) => (
            <button key={i} type="button" role="radio" aria-checked={i === gewaehlt} className={i === gewaehlt ? "an" : undefined} onClick={() => waehlen(i)}>
              {a.name}
            </button>
          ))}
        </div>
      </div>
      {zeile}
    </>
  );
}

/**
 * Ein Tag zum Ändern. Gespeichert wird erst beim Verlassen des Felds (oder
 * mit Enter) – nicht bei jeder Ziffer: Beim Tippen der Jahreszahl entstünden
 * sonst Zwischentage („0202“), und eine schon verschickte Mail würde wegen
 * eines halben Datums wieder geöffnet. Solange gespeichert wird, ist der
 * Mail-Knopf gesperrt (die Karte zeigt dann noch den alten Stand).
 */
export function TagFeld({
  name,
  wert,
  gesperrt,
  speichern,
  titel,
}: {
  name: string;
  wert: string;
  gesperrt: boolean;
  speichern: (tag: string) => void;
  titel?: string;
}) {
  const [text, setText] = useState(wert);
  const [stand, setStand] = useState(wert);
  // Kommt vom Server ein anderer Stand (neu geladen), gilt er.
  if (stand !== wert) {
    setStand(wert);
    setText(wert);
  }
  const fertig = () => {
    if (text !== wert && /^\d{4}-\d{2}-\d{2}$/.test(text) && text >= "2000-01-01") speichern(text);
  };
  return (
    <label className="crm-feld">
      {name}
      <input
        className="crm-eingabe"
        type="date"
        value={text}
        disabled={gesperrt}
        {...(titel ? { title: titel } : {})}
        onChange={(e) => setText(e.target.value)}
        onBlur={fertig}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            fertig();
          }
        }}
      />
    </label>
  );
}

/** „Text kopieren“ – wenn es keine Mail-Adresse gibt oder der Text nicht in den Link passt. */
export function TextKopieren({ text, wo }: { text: string; wo: KartenName }) {
  return (
    <button
      type="button"
      className="crm-knopf rand klein"
      onClick={() => {
        void (async () => {
          try {
            await navigator.clipboard.writeText(text);
            toastZeigen("Text kopiert – in die Mail einfügen");
          } catch (f) {
            unerwartetMelden(f, wo);
            toastZeigen("Kopieren ging nicht – bitte den Text oben markieren und kopieren.", "fehler");
          }
        })();
      }}
    >
      <Ikon name="datei" groesse="k" /> Text kopieren
    </button>
  );
}

/** „schon erledigt“ – erst ein Link, dann die Rückfrage in einer Zeile (wie in „Zu tun“). */
export function SchonErledigtKnopf({ frage, sendet, los }: { frage: string; sendet: boolean; los: () => void }) {
  const [fragen, setFragen] = useState(false);
  if (!fragen) {
    return (
      <button type="button" className="crm-knopf leise klein" onClick={() => setFragen(true)} disabled={sendet}>
        schon erledigt
      </button>
    );
  }
  return (
    <span className="ablauf-rueckfrage" role="group" aria-label="Schon erledigt?">
      <span>{frage}</span>
      <button type="button" className="crm-knopf dunkel klein" onClick={los} disabled={sendet}>
        Ja, als erledigt vermerken
      </button>
      <button type="button" className="crm-knopf rand klein" onClick={() => setFragen(false)} disabled={sendet}>
        Abbrechen
      </button>
    </span>
  );
}

/**
 * Über den Schritten einer Mail an Lieferant oder Dachpartie: was das CRM noch
 * nicht kann. Gernot im Augentest E3c (3.11.2026): „da muss unbedingt eine Info
 * dazu“ – sonst glaubt man, die Dateien gingen mit, und beim Bestellen passieren
 * Fehler. Weg B (das CRM verschickt selbst, samt Dateien) kommt mit dem Vertrag
 * (PLAN.md, E3c, „Mail“).
 */
export function NochVonHand() {
  return (
    <Tipp>
      <b>Noch von Hand:</b> Das CRM schreibt die Mail vor, kann die Dateien aber noch nicht mitschicken – die hängst du in deinem Mail-Programm selbst an. In
      einer späteren Ausbaustufe verschickt das CRM die Mail samt Dateien selbst.
    </Tipp>
  );
}

/** Ein nummerierter Schritt einer Mail: Dateien herunterladen, Mail öffnen und abschicken, abhaken. */
export function MailSchritt({ nr, titel, children }: { nr: number; titel: string; children: ReactNode }) {
  return (
    <li className="ablauf-schritt" aria-label={`Schritt ${nr}: ${titel}`}>
      <span className="ablauf-nr" aria-hidden="true">
        {nr}
      </span>
      <div className="ablauf-schritt-inhalt">
        <b>{titel}</b>
        {children}
      </div>
    </li>
  );
}

/**
 * Den Excel-Report von K2 hochladen oder ersetzen – eine Datei wählen, fertig
 * (k2-excel.ts). Bis 3.11.2026 hieß der Knopf „Excel anhängen“ – neben den
 * Dateien „zum Anhängen“ an die Mail las Gernot das als dasselbe.
 */
export function K2ExcelKnopf({ anlageId, da, fertig }: { anlageId: string; da: boolean; fertig: () => void }) {
  const [sendet, setSendet] = useState(false);
  const [meldung, setMeldung] = useState<string | null>(null);

  async function gewaehlt(datei: File | undefined) {
    if (!datei) return;
    setSendet(true);
    setMeldung(null);
    try {
      await k2ExcelAnhaengen(anlageId, datei);
      toastZeigen(da ? "K2-Liste (Excel) ersetzt" : "K2-Liste (Excel) hochgeladen");
      anderenFensternMelden(anlageId);
      fertig();
    } catch (f) {
      setMeldung(f instanceof Error && !(f instanceof ApiFehler) && !(f instanceof OhneVerbindung) ? f.message : meldungAus(f));
    } finally {
      setSendet(false);
    }
  }

  return (
    <>
      <label className={`crm-knopf rand klein${sendet ? " laedt" : ""}`} title="Den Excel-Report von K2 (.xlsx) vom Computer ins CRM hochladen – die Unterkonstruktionsliste für den Lieferanten">
        {sendet ? <span className="kreisel" aria-hidden="true" /> : <Ikon name="hochladen" groesse="k" />} {da ? "Excel ersetzen" : "Excel hochladen"}
        <input
          type="file"
          accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          className="datei-waehler"
          aria-label={da ? "K2-Liste (Excel) ersetzen" : "K2-Liste (Excel) hochladen"}
          disabled={sendet}
          onChange={(e) => {
            void gewaehlt(e.target.files?.[0]);
            e.target.value = "";
          }}
        />
      </label>
      {meldung && (
        <span className="crm-fehler-satz" role="alert">
          {meldung}
        </span>
      )}
    </>
  );
}

/**
 * Fängt einen Fehler beim Anzeigen einer Karte ab: Die Akte bleibt stehen, die
 * Karte sagt es in einem Satz, und die Fehlerwache bekommt nur die Art und die
 * Stellen im Code – nie, was in der Karte stand (Adresse, Mail).
 */
export class KartenGrenze extends Component<{ wo: KartenName; children: ReactNode }, { kaputt: boolean }> {
  override state = { kaputt: false };

  static getDerivedStateFromError(): { kaputt: boolean } {
    return { kaputt: true };
  }

  override componentDidCatch(fehler: unknown): void {
    fehlerMelden("crm", ablaufFehlerFuerWache(fehler, this.props.wo));
  }

  override render(): ReactNode {
    if (this.state.kaputt) {
      return (
        <p className="crm-fehler-satz" role="alert">
          Die Karte „{this.props.wo}“ ließ sich nicht anzeigen – bitte die Seite neu laden.
        </p>
      );
    }
    return this.props.children;
  }
}
